/**
 * Composable for managing avatar functionality
 * Handles avatar creation, editing, rendering, and management
 */
import { ref } from 'vue';
import { API } from '@/constants/urls';

/**
 * Create avatar management composable
 * 
 * @param {string} avaturnUrl - The Avaturn API URL
 * @param {string} apiToken - The API token for authentication
 * @param {Function} setViewModeFn - Function to set the current view mode
 * @returns {Object} - Avatar management methods and state
 */
export function useAvatar(
  avaturnUrl = API.AVATURN, 
  apiToken = API.TOKEN, 
  setViewModeFn
) {
  // Loading state
  const isLoading = ref(false);
  // User avatars collection
  const userAvatars = ref([]);
  // Flag to track if we're waiting for renders to complete
  const waitForRender = ref(false);
  
/**
 * Get user avatars from Avaturn API
 */
const getUserAvatars = async (userId) => {
  try {
    console.log('Fetching avatars for user:', userId);
    isLoading.value = true; // Set loading state when fetching starts
    
    const response = await fetch(`${avaturnUrl}/users/${userId}/avatars`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${apiToken}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    let data = await response.json();
    console.log('Raw avatar data:', data);
    
    // Process avatars and ensure they have render_url values
    data = data.map(avatar => {
      if (avatar.status === "ready" && !avatar.render_url) {
        avatar.render_url = `${avaturnUrl}/renders/${avatar.id}/final.png`;
      }
      return avatar;
    });
    
    console.log('Processed user avatars:', data);
    userAvatars.value = data;
    isLoading.value = false; // Clear loading when done
    return data;
  } catch (error) {
    console.error('Error fetching user avatars:', error.message);
    isLoading.value = false; // Also clear loading on error
    return [];
  }
};

  
  
  
  /**
   * Process avatars to check if renders are needed
   */
  const processAvatars = async (userId, logger, getLoggerFn, updateLoggerFn) => {
    try {
      isLoading.value = true;
      
      // Ensure logger.avatars is an array
      if (!logger.avatars) {
        logger.avatars = [];
      }
      
      // Get updated avatars from API
      await getUserAvatars(userId);
      
      let renderNeeded = false;
      console.log('Processing avatars:', userAvatars.value, logger.avatars);
      
      // Sync avatars between userAvatars and logger
      for (const userAvatar of userAvatars.value) {
        const existsInLogger = logger.avatars.some(loggerAvatar => loggerAvatar.id === userAvatar.id);
        
        if (!existsInLogger) {
          // Add new avatar to logger
          logger.avatars.push({...userAvatar});
          await renderAvatarImage(userAvatar.id);
          renderNeeded = true;
        } else {
          // Update existing avatar in logger
          const logAvatarIndex = logger.avatars.findIndex(a => a.id === userAvatar.id);
          if (logAvatarIndex >= 0) {
            // Only request render if needed
            if (!logger.avatars[logAvatarIndex].render_url) {
              await renderAvatarImage(userAvatar.id);
              renderNeeded = true;
            }
            // Update logger avatar with latest data
            logger.avatars[logAvatarIndex] = {...logger.avatars[logAvatarIndex], ...userAvatar};
          }
        }
      }
      
      // Update the logger
      if (renderNeeded) {
        await updateLoggerFn(userId);
        waitForRender.value = true;
        startRenderCheckInterval(userId, getLoggerFn);
      }
      
      // Final check of render URLs validity
      await validateRenderUrls();
      
      isLoading.value = false;
      return renderNeeded;
    } catch (error) {
      console.error('Error processing avatars:', error);
      isLoading.value = false;
      return false;
    }
  };
  
  
  /**
   * Start interval to check if all renders are complete
   */
  const startRenderCheckInterval = (userId, getLoggerFn) => {
    const intervalId = setInterval(async () => {
      try {
        // 1) Get the latest logger data
        const loggerData = await getLoggerFn(userId);

        // 2) Check if every avatar has a render_url
        const allRendered = loggerData.avatars && loggerData.avatars.every(a => a.render_url);
        console.log('All renders completed:', allRendered);

        if (allRendered) {
          // 3) Stop checking
          waitForRender.value = false;
          clearInterval(intervalId);
          console.log("All avatar renders completed.");

          // 4) Re-fetch user avatars from Avaturn 
          //    (assuming this function populates userAvatars.value)
          const updatedAvatars = await getUserAvatars(userId);
          console.log('Refetched Avaturn avatars:', updatedAvatars);

          // OPTIONAL: If your <AvatarList> uses logger.avatars to display,
          // copy the final render_url from updatedAvatars into logger.avatars.
          updatedAvatars.forEach(ua => {
            const matching = loggerData.avatars.find(l => l.id === ua.id);
            if (matching && ua.render_url) {
              matching.render_url = ua.render_url;
            }
          });

          // Now loggerData.avatars has final .render_url for each avatar
          // so <AvatarList :avatars="logger.avatars" /> can display them.
        }
      } catch (error) {
        console.error('Error checking render status:', error.message);
      }
    }, 30000);
  };
  
  /**
   * Check if render URLs are valid and fix them if needed
   */
  const validateRenderUrls = async () => {
    if (!userAvatars.value.length) return;
    
    for (let i = 0; i < userAvatars.value.length; i++) {
      const avatar = userAvatars.value[i];
      
      // If no render_url, request one
      if (!avatar.render_url && avatar.status === "ready") {
        console.log('Requesting render for avatar:', avatar.id);
        const renderData = await renderAvatarImage(avatar.id);
        if (renderData) {
          // We'll check later if the render is done
          userAvatars.value[i] = { ...avatar, render_url: `${avaturnUrl}/renders/${avatar.id}/final.png` };
        }
      } 
      // If has render_url but it may not be valid, verify it
      else if (avatar.render_url) {
        const isValid = await checkRenderUrl(avatar.render_url);
        if (!isValid) {
          console.log('Render URL invalid, requesting new render:', avatar.id);
          await renderAvatarImage(avatar.id);
        }
      }
    }
  };

  /**
   * Request rendering of an avatar image
   */
  const renderAvatarImage = async (avatarId) => {
    try {
      const response = await fetch(
        `${avaturnUrl}/renders/new?format=png&avatar_id=${avatarId}&scene=common/transparent_half_body`, 
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${apiToken}`,
            'Content-Type': 'application/json'
          }
        }
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Render requested:', data);
      return data;
    } catch (error) {
      console.error('Error requesting avatar render:', error.message);
      return null;
    }
  };
  
  /**
   * Create a new Avaturn session for editing an existing avatar
   * @param {string} userId - plain string user ID (no reactivity)
   * @param {string} avatar_id - The avatar ID to edit
   * @param {object} userStore - The user store (so we can update .sessionId, .avaturnUrl, etc.)
   */
  const createNewSession = async (userId, avatar_id, userStore) => {
    try {
      isLoading.value = true;
      
      const response = await fetch(`${avaturnUrl}/sessions/new`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${apiToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user_id: userId,
          config: {
            type: "edit_existing",
            avatar_id
          }
        })
      });
      
      if (!response.ok) {
        isLoading.value = false;
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('New session created:', data);
      
      // Update userStore fields
      // If these are Refs, do: userStore.sessionId.value = data.id
      userStore.sessionId.value = data.id;
      userStore.avaturnUrl.value = data.url;
      
      // Switch to edit mode
      setViewModeFn('edit');
      isLoading.value = false;
      return data;
    } catch (error) {
      isLoading.value = false;
      console.error('Error creating new session:', error.message);
      return null;
    }
  };
  
  /**
   * Create a new Avaturn user
   */
  const createNewUser = async () => {
    try {
      const response = await fetch(`${avaturnUrl}/users/new`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${apiToken}`,
          'Content-Type': 'application/json'
        },
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('New user created:', data);
      return data;
    } catch (error) {
      console.error('Error creating new user:', error.message);
      return null;
    }
  };
  
  /**
   * Delete an avatar
   */
  const deleteAvatar = async (userId, avatar, logger, updateLoggerFn) => {
    try {
      const response = await fetch(`${avaturnUrl}/users/${userId}/avatars/${avatar.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${apiToken}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Avatar deleted:', data);
      
      // Remove from logger
      logger.avatars = logger.avatars.filter(a => a.id !== avatar.id);
      
      // Update DB and refresh
      await updateLoggerFn(userId);
      await getUserAvatars(userId);
      return true;
    } catch (error) {
      console.error('Error deleting avatar:', error.message);
      return false;
    }
  };
  
  /**
   * Check if a URL exists
   */
  const checkRenderUrl = async (url) => {
    try {
      const response = await fetch(url);
      console.log('Render URL check result:', response);
      return response.ok;
    } catch (error) {
      console.error('Error checking render URL:', error.message);
      return false;
    }
  };
  
  /**
   * Handle avatar loaded from Avaturn
   */
  const handleAvatarLoaded = (res, userStore) => {
    userStore.userAvatarUrl.value = res.url;
    userStore.userBodyId.value = res.bodyId;
    userStore.avatarGender.value = res.gender;
    setViewModeFn('scene');
  };
  
  return {
    isLoading,
    userAvatars,
    waitForRender,
    getUserAvatars,
    processAvatars,
    createNewSession,
    createNewUser,
    deleteAvatar,
    checkRenderUrl,
    startRenderCheckInterval,
    handleAvatarLoaded,
    renderAvatarImage
  };
}
